import React, { memo, useCallback, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import AutocompleteCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/AutocompleteCDS';
import PropTypes from 'prop-types';

import {
  entitySelectorContainerStyles,
  entitySelectorHelperStyles,
  entitySelectorInputStyles,
} from './EntitySelector.styles';
import { getEntitySelectorHelperMessage } from './EntitySelector.utils';

const EntitySelector = ({
  options,
  selectedId = null,
  onSelect,
  isDisabled = false,
  label,
  placeholder,
  helperText,
  emptyText,
  dataTestId,
  name,
}) => {
  const autocompleteOptions = useMemo(
    () => options.map(option => ({ name: option.fullName, value: option.id })),
    [options],
  );

  const selectedOption = useMemo(
    () => autocompleteOptions.find(option => option.value === selectedId) ?? null,
    [autocompleteOptions, selectedId],
  );

  const onChange = useCallback(
    (_event, option) => {
      onSelect(option?.value ?? null);
    },
    [onSelect],
  );

  const hasOptions = !!autocompleteOptions.length;
  const helperMessage = getEntitySelectorHelperMessage({
    hasOptions,
    selectedOption,
    helperText,
    emptyText,
  });

  return (
    <Box sx={entitySelectorContainerStyles}>
      {!!helperMessage && (
        <Typography color="textSecondary" sx={entitySelectorHelperStyles} variant="regular12">
          {helperMessage}
        </Typography>
      )}
      <Box sx={entitySelectorInputStyles}>
        <AutocompleteCDS
          disableClearable
          dataTestId={dataTestId}
          disabled={isDisabled || !hasOptions}
          label={label}
          name={name}
          optionKey="value"
          optionName="name"
          options={autocompleteOptions}
          placeholder={placeholder}
          required={hasOptions}
          value={selectedOption}
          onChange={onChange}
        />
      </Box>
    </Box>
  );
};

EntitySelector.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
  helperText: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      fullName: PropTypes.string,
    }),
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  selectedId: PropTypes.string,
};

export default memo(EntitySelector);
